import type { NavigateFunction } from 'react-router-dom';
import Paths from '@/configs/router/paths';
import { useRefs } from '@/scripts/hooks';
import { chatApis } from '@/configs/apis/chat';
import { useSessionContext } from '@/contexts/session/context';
import type { User } from '@/configs/apis/user';
import { serverTime } from '../useSystemTime/utils';
import type { ChatMessage } from '../useChatMessages/types';
import type { UseSingleEncryptionResult } from '../useSingleEncryption/types';
import type { UseGroupEncryptionResult } from '../useGroupEncryption/types';
import type { ChatChatId, ChatInfo, ChatInfoMap, DBChatParams } from './types';
import { type Chat, type ChatQuery, type ChatParams, ChatCategory } from './types';
import { genStateChat, getChatInfoByMap } from './utils';

interface ChatsState {
  chats: Chat[];
  chatInfoMap: ChatInfoMap;
  chatReadCountMap: Record<ChatChatId, number>;
  activatedChatIds: ChatChatId[];
  activatedChatId: ChatChatId | undefined;
  activatedChats: Chat[];
  activatedChat: Chat | undefined;
  oldActivatedChat: Chat | undefined;
  sort: boolean;
}

type ChatsStateActions =
  | { type: 'init' }
  | { type: 'insertChats'; payload: ChatParams[] }
  | { type: 'removeChats'; payload: ChatChatId[] }
  | { type: 'updateChats'; payload: ChatParams[] }
  | { type: 'updateChatReadCount'; payload: { chatId: ChatChatId; count: number } }
  | { type: 'setActivatedChatId'; payload: ChatChatId }
  | {
      type: 'getChatInfo';
      payload: ChatQuery;
      cb: (info: ChatInfo | undefined) => void;
    };

function genChatsState(): ChatsState {
  return {
    chats: [],
    chatInfoMap: {},
    chatReadCountMap: {},
    activatedChatIds: [],
    activatedChatId: undefined,
    activatedChats: [],
    activatedChat: undefined,
    oldActivatedChat: undefined,
    sort: false,
  };
}

export function useChatsState() {
  const [state, dispatch] = useReducer(
    (state: ChatsState, action: ChatsStateActions) => {
      let state_ = { ...state };
      switch (action.type) {
        case 'init':
          state_ = genChatsState();
          break;

        case 'insertChats': {
          state_.chats = [...state_.chats];
          const adds: Chat[] = [];
          action.payload.forEach((chat) => {
            const chatInfo = getChatInfoByMap(state_.chatInfoMap, chat);
            if (chatInfo) {
              state_.chats.splice(chatInfo.index, 1, { ...chatInfo.chat, ...chat });
            } else {
              adds.push(genStateChat(chat));
            }
          });
          state_.chats = state_.chats.concat(adds);
          break;
        }

        case 'removeChats': {
          const chatIds = action.payload;
          state_.chats = state_.chats.filter((chat) => !chatIds.includes(chat.chat_id));

          if (state_.activatedChatId && chatIds.includes(state_.activatedChatId)) {
            state_.activatedChatId = undefined;
          }

          if (state_.activatedChatIds.length) {
            state_.activatedChatIds = state_.activatedChatIds.filter((id) => {
              return !chatIds.includes(id);
            });
          }
          break;
        }

        case 'updateChats': {
          state_.chats = state_.chats.map((chat) => {
            return {
              ...chat,
              ...action.payload.find((c) => c.chat_id === chat.chat_id),
            };
          });
          break;
        }

        case 'updateChatReadCount': {
          state_.chatReadCountMap[action.payload.chatId] = action.payload.count;
          break;
        }

        case 'setActivatedChatId': {
          const activatedChatId = action.payload;

          const activatedChatIds = state.activatedChatIds.includes(activatedChatId)
            ? state.activatedChatIds
            : [...state.activatedChatIds, activatedChatId];

          if (activatedChatId !== state_.activatedChatId) {
            state_.oldActivatedChat = state_.activatedChat;
          }
          state_.activatedChatIds = activatedChatIds;
          state_.activatedChatId = activatedChatId;
          break;
        }

        case 'getChatInfo': {
          action.cb(getChatInfoByMap(state.chatInfoMap, action.payload));
          return state;
        }
      }

      state_.chats = [...state_.chats].sort((a, b) =>
        a.update_at && b.update_at ? b.update_at - a.update_at : a.update_at ? -1 : 1
      );

      const chatInfoMap: ChatInfoMap = {};
      state_.chats.forEach((chat, index) => {
        chatInfoMap[chat.chat_id] = {
          index,
          chat,
        };
      });
      state_.chatInfoMap = chatInfoMap;

      state_.activatedChats = state_.chats.length
        ? state_.activatedChatIds.map((id) => state_.chatInfoMap[id].chat)
        : [];

      state_.activatedChat =
        state_.chats.length && state_.activatedChatId
          ? state_.chatInfoMap[state_.activatedChatId].chat
          : undefined;

      return state_;
    },
    genChatsState()
  );

  const getChatInfo = useCallback((chatKey: ChatQuery) => {
    return new Promise<ChatInfo | undefined>((resolve) => {
      dispatch({ type: 'getChatInfo', payload: chatKey, cb: resolve });
    });
  }, []);

  const getChat = useCallback(
    async (chatKey: ChatQuery) => {
      return (await getChatInfo(chatKey))?.chat;
    },
    [getChatInfo]
  );

  const insertChats = useCallback((chats: ChatParams[]) => {
    dispatch({ type: 'insertChats', payload: chats });
  }, []);

  const removeChats = useCallback((ids: ChatChatId[]) => {
    dispatch({ type: 'removeChats', payload: ids });
  }, []);

  const updateChats = useCallback((chats: ChatParams[]) => {
    dispatch({ type: 'updateChats', payload: chats });
  }, []);

  const activeChat = useCallback((id: ChatChatId) => {
    dispatch({ type: 'setActivatedChatId', payload: id });
  }, []);

  const initChats = useCallback(() => {
    dispatch({ type: 'init' });
  }, []);

  const updateChatReadCount = useCallback((chatId: ChatChatId, count: number) => {
    dispatch({ type: 'updateChatReadCount', payload: { chatId, count } });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      dispatch,
      insertChats,
      removeChats,
      updateChats,
      getChatInfo,
      getChat,
      activeChat,
      initChats,
      updateChatReadCount,
    }),
    [
      state,
      insertChats,
      removeChats,
      updateChats,
      getChatInfo,
      getChat,
      activeChat,
      initChats,
      updateChatReadCount,
    ]
  );

  return value;
}

export function useChatsDB() {
  const getChats = useCallback(
    async (userId: User['userId'], limit: number, offset: number): Promise<Chat[]> => {
      return chatApis.getChats(userId, limit, offset);
    },
    []
  );

  const getChat = useCallback((params: ChatQuery) => {
    return chatApis.getChat(params);
  }, []);

  const insertChats = useCallback((chatPartials: DBChatParams[]) => {
    return chatApis.insertChats(chatPartials);
  }, []);

  const removeChats = useCallback((ids: ChatChatId[]) => {
    return chatApis.removeChats(ids);
  }, []);

  const updateChats = useCallback((chatPartials: DBChatParams[]) => {
    return chatApis.updateChats(chatPartials);
  }, []);

  const value = useMemo(
    () => ({
      getChats,
      getChat,
      insertChats,
      updateChats,
      removeChats,
    }),
    [insertChats, updateChats, getChat, getChats, removeChats]
  );
  return value;
}

export function useChats({
  singleEncryption,
}: {
  singleEncryption: UseSingleEncryptionResult;
  groupEncryption: UseGroupEncryptionResult;
}) {
  const {
    session: { user },
  } = useSessionContext();
  const navigate = useNavigate();
  const chatsState = useChatsState();
  const chatsDB = useChatsDB();

  const refs = useRefs<{
    user: User | null;
    chatsState: ReturnType<typeof useChatsState>;
    chatsDB: ReturnType<typeof useChatsDB>;
    navigate: NavigateFunction;
    loadChats: (
      currentUser: User,
      size: number,
      current: number
    ) => Promise<Chat[] | undefined>;
    initChats: () => void;
    singleEncryption: UseSingleEncryptionResult;
  }>({
    user,
    chatsState,
    chatsDB,
    navigate,
    loadChats: () => Promise.resolve([]),
    initChats: () => {},
    singleEncryption,
  });

  const loadChats = useCallback(
    async (currentUser: User, limit: number, offset: number) => {
      const { chatsDB, chatsState, singleEncryption } = refs.current;
      return await chatsDB
        .getChats(currentUser.userId, limit, offset)
        .then(async (chats) => {
          await Promise.all(
            chats.map((chat) => {
              if (chat.category === ChatCategory.SINGLE) {
                singleEncryption.getTargetChatPubKeys(chat.receiver_id);
              }
              chatApis
                .getUnreadMessageCount(chat.chat_id, chat.last_read_message_id)
                .then((count) => {
                  chatsState.updateChatReadCount(chat.chat_id, count);
                });
              chat.last_read_new_message_id = chat.last_read_message_id;
              return chatApis.deleteExpiredMessages(chat.chat_id, serverTime.time);
            })
          );
          chatsState.insertChats(chats);
          return chats;
        });
    },
    [refs]
  );

  refs.current.loadChats = loadChats;

  const insertChats = useCallback(
    async (chatsParams: ChatParams[]) => {
      const { chatsDB, chatsState } = refs.current;
      await chatsDB.insertChats(chatsParams);
      chatsState.insertChats(chatsParams);
    },
    [refs]
  );

  const removeChats = useCallback(
    async (ids: ChatChatId[]) => {
      const { chatsDB, chatsState } = refs.current;
      await chatsDB.removeChats(ids);
      chatsState.removeChats(ids);
    },
    [refs]
  );

  const updateChats = useCallback(
    async (chats: ChatParams[]) => {
      const { chatsDB, chatsState } = refs.current;
      await chatsDB.updateChats(chats);
      chatsState.updateChats(chats);
    },
    [refs]
  );

  const activeChat = useCallback(
    async (id: ChatChatId) => {
      const { navigate, chatsState } = refs.current;
      const chat = await chatsState.getChat({ chat_id: id });
      if (chat) {
        chatsState.activeChat(id);
        navigate(Paths.MESSAGES_CHATS);
        return true;
      }
    },
    [refs]
  );

  const initChats = useCallback(async () => {
    // const { chatsState, loadChats } = refs.current;
    const { chatsState } = refs.current;
    chatsState.initChats();
    // await loadChats();
  }, [refs]);

  const getChat = useCallback(
    async (params: ChatQuery) => {
      return (await chatsState.getChat(params)) || (await chatsDB.getChat(params));
    },
    [chatsDB, chatsState]
  );

  const updateLastReadMessages = useCallback(
    async (paramsList: Array<{ chat: Chat; message: ChatMessage }>) => {
      const chatParamsList: ChatParams[] = [];
      paramsList.forEach(({ chat, message }) => {
        if (!chat.last_read_message_id || chat.last_read_message_id < message.id) {
          chatParamsList.push({
            chat_id: chat.chat_id,
            last_read_message_id: message.id,
          });
        }
      });
      if (chatParamsList.length) {
        await insertChats(chatParamsList);
      }
    },
    [insertChats]
  );

  refs.current.initChats = initChats;

  const value = useMemo(
    () => ({
      loadChats,
      initChats,
      insertChats,
      updateChats,
      activeChat,
      removeChats,
      getChat,
      chatsState,
      chatsDB,
      updateLastReadMessages,
    }),
    [
      activeChat,
      chatsDB,
      chatsState,
      getChat,
      initChats,
      insertChats,
      loadChats,
      removeChats,
      updateChats,
      updateLastReadMessages,
    ]
  );

  useLayoutEffect(() => {
    if (user) {
      const { initChats } = refs.current;
      initChats();
    }
  }, [refs, user]);

  return value;
}
